import React, { useState } from 'react';
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Button, Typography, Box, Stack, CircularProgress,
    List, ListItem, ListItemText, ListItemIcon, Divider, Alert
} from '@mui/material';
import { CloudUpload, Image as ImageIcon, CheckCircle, Error as ErrorIcon } from '@mui/icons-material';
import { toast } from 'react-toastify';
import api from '../utils/Api';

const BulkPictureModal = ({ open, onClose, onSuccess }) => {
    const [files, setFiles] = useState([]);
    const [loading, setLoading] = useState(false);
    const [results, setResults] = useState(null);

    const handleFileChange = (e) => {
        if (e.target.files) {
            setFiles(Array.from(e.target.files));
        }
    };

    const handleClose = () => {
        setFiles([]);
        setResults(null);
        onClose();
    };

    const handleSubmit = async () => {
        if (files.length === 0) {
            toast.error('Please select at least one image file.');
            return;
        }

        if (files.length > 100) {
            toast.error('You can only upload up to 100 images at a time.');
            return;
        }

        setLoading(true);
        setResults(null);

        const formData = new FormData();
        files.forEach((file) => {
            formData.append('pictures', file);
        });

        try {
            const res = await api.post('/students/bulk-pictures', formData, {
                headers: { 'Content-Type': 'multipart/form-data' }
            });
            setResults(res.data);
            if (res.data.summary.successCount > 0) {
                toast.success(`Successfully uploaded ${res.data.summary.successCount} pictures.`);
                if (onSuccess) onSuccess();
            }
        } catch (err) {
            console.error('Error uploading bulk pictures:', err);
            toast.error(err.response?.data?.message || 'Failed to upload pictures.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={handleClose} fullWidth maxWidth="md">
            <DialogTitle sx={{ fontWeight: 'bold' }}>Bulk Upload Student Pictures</DialogTitle>
            <DialogContent dividers>
                {!results ? (
                    <Box sx={{ mb: 2 }}>
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2" sx={{ fontWeight: 'bold' }}>Instructions:</Typography>
                            <Typography variant="body2" gutterBottom>
                                Please name your image files exactly as the student's <strong>Full Name</strong> or <strong>Admission Number</strong>.
                            </Typography>
                            <Typography variant="body2">
                                Example: <code>John Doe.jpg</code> or <code>ADM-001.png</code>
                            </Typography>
                            <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                                (Max 100 images per upload, up to 2MB each recommended).
                            </Typography>
                        </Alert>
                        <Stack alignItems="center" justifyContent="center" sx={{ p: 4, border: '2px dashed #ccc', borderRadius: 2, bgcolor: '#f9f9f9' }}>
                            <CloudUpload sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="bulk-picture-upload"
                                multiple
                                type="file"
                                onChange={handleFileChange}
                            />
                            <label htmlFor="bulk-picture-upload">
                                <Button variant="contained" component="span" disabled={loading}>
                                    Select Image Files
                                </Button>
                            </label>
                            {files.length > 0 && (
                                <Typography variant="body2" sx={{ mt: 2, fontWeight: 'bold' }}>
                                    {files.length} file(s) selected
                                </Typography>
                            )}
                        </Stack>
                    </Box>
                ) : (
                    <Box>
                        <Typography variant="h6" gutterBottom>Upload Summary</Typography>
                        <Stack direction="row" spacing={4} sx={{ mb: 3 }}>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="primary">{results.summary.total}</Typography>
                                <Typography variant="body2" color="text.secondary">Total Processed</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="success.main">{results.summary.successCount}</Typography>
                                <Typography variant="body2" color="text.secondary">Successful</Typography>
                            </Box>
                            <Box sx={{ textAlign: 'center' }}>
                                <Typography variant="h4" color="error.main">{results.summary.errorCount}</Typography>
                                <Typography variant="body2" color="text.secondary">Failed</Typography>
                            </Box>
                        </Stack>
                        
                        {results.results.errors.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" color="error" fontWeight="bold">Errors:</Typography>
                                <List dense>
                                    {results.results.errors.map((err, idx) => (
                                        <ListItem key={idx}>
                                            <ListItemIcon><ErrorIcon color="error" /></ListItemIcon>
                                            <ListItemText primary={err.file} secondary={err.message} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}

                        {results.results.success.length > 0 && (
                            <Box sx={{ mt: 2 }}>
                                <Typography variant="subtitle1" color="success.main" fontWeight="bold">Successfully Uploaded:</Typography>
                                <List dense>
                                    {results.results.success.map((succ, idx) => (
                                        <ListItem key={idx}>
                                            <ListItemIcon><CheckCircle color="success" /></ListItemIcon>
                                            <ListItemText primary={succ.name} secondary={`Matched via: ${succ.file}`} />
                                        </ListItem>
                                    ))}
                                </List>
                            </Box>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
                {!results ? (
                    <>
                        <Button onClick={handleClose} disabled={loading}>Cancel</Button>
                        <Button onClick={handleSubmit} variant="contained" disabled={files.length === 0 || loading} startIcon={loading ? <CircularProgress size={20} /> : null}>
                            {loading ? 'Uploading...' : 'Upload Pictures'}
                        </Button>
                    </>
                ) : (
                    <Button onClick={handleClose} variant="contained">Close</Button>
                )}
            </DialogActions>
        </Dialog>
    );
};

export default BulkPictureModal;
